/**
 * LRUMap
 *
 * A fixed-size Least Recently Used (LRU) map.
 * Stores a limited number of entries and automatically
 * evicts the least recently accessed item when the limit
 * is exceeded. Prevents memory growth while
 * keeping frequently used data hot in memory.
 */

class LRUMap<K, V> {
  private map = new Map<K, V>()

  constructor(private maxSize: number) {}

  get(key: K) {
    const value = this.map.get(key)
    if (!value) return undefined

    // move to newest
    this.map.delete(key)
    this.map.set(key, value)

    return value
  }

  set(key: K, value: V) {
    if (this.map.has(key)) {
      this.map.delete(key)
    }

    this.map.set(key, value)

    if (this.map.size > this.maxSize) {
      const oldest = this.map.keys().next().value
      if (oldest !== undefined) {
        this.map.delete(oldest)
      }
    }
  }

  has(key: K) {
    return this.map.has(key)
  }
}

export default LRUMap
